// Board — the numbers a founder says out loud, and where each one comes from.
//
// One component, four screens. The payload shape is uniform (a headline strip
// plus a screen-specific body plus a guard), so the skeleton is shared and only
// the body differs — the alternative was four near-identical files drifting
// apart, which is how the classic console got to 16,600 lines of CSS.
//
// THE EDITORIAL RULE THIS SCREEN ENFORCES, and it is the reason it exists at
// all: a figure that crosses one of the three regime boundaries is shown WITH
// the boundary, never as a single total. The server refuses to return a
// lifetime total without its split; this file refuses to render one without it.
// Both halves are deliberate — either alone would rot the first time someone
// added a tile in a hurry before a meeting.
//
// NOTHING IS COMPUTED HERE beyond formatting and bar widths. Every figure
// arrives from /api/insights/board/:screen. If you find yourself writing
// arithmetic in this file, it belongs in the route, next to the SQL that can
// be read alongside it.

import React from 'react';
import { PageHead, Loading, ErrorState, Button } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import s from './Board.module.css';

const inr = (v, dp = 2) => (v == null ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
const inr0 = (v) => inr(v, 0);
const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
const pct = (v, dp = 1) => (v == null ? '—' : `${Number(v).toFixed(dp)}%`);

function Headlines({ items }) {
  if (!items?.length) return null;
  return (
    <div className={s.heads}>
      {items.map((h) => (
        <div key={h.label} className={s.head}>
          <span className={s.headLabel}>{h.label}</span>
          <span className={s.headValue}>{h.value ?? '—'}</span>
          {h.note && <span className={s.headNote}>{h.note}</span>}
        </div>
      ))}
    </div>
  );
}

function Card({ title, sub, children }) {
  return (
    <section className={s.card}>
      {(title || sub) && (
        <div className={s.cardHead}>
          {title && <h2 className={s.cardTitle}>{title}</h2>}
          {sub && <p className={s.cardSub}>{sub}</p>}
        </div>
      )}
      {children}
    </section>
  );
}

function Table({ cols, rows, getKey }) {
  if (!rows?.length) return <p className={s.cardSub}>Nothing to show.</p>;
  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead>
          <tr>{cols.map((c) => <th key={c.key} style={c.num ? { textAlign: 'right' } : undefined}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={getKey ? getKey(r, i) : i}>
              {cols.map((c) => (
                <td key={c.key} className={c.num ? s.num : (c.muted ? s.muted : undefined)}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── 01 · the estate ─────────────────────────────────────────────────── */
function Estate({ d }) {
  const top = Math.max(1, ...(d.ladder || []).map((r) => r.count));
  return (
    <>
      <Card
        title="The ladder"
        sub="Six answers to “how many screens do you have”, all true, all different. Lead with the last rung."
      >
        <div className={s.ladder}>
          {(d.ladder || []).map((r, i) => (
            <React.Fragment key={r.rung}>
              <div className={s.rung}>
                <span className={s.rungName}>{r.rung}</span>
                <div className={s.rungBarWrap}>
                  <div
                    className={`${s.rungBar} ${i === d.ladder.length - 1 ? s.last : ''}`}
                    style={{ width: `${(r.count / top) * 100}%` }}
                  />
                </div>
                <span className={s.rungCount}>{n(r.count)}</span>
                <span className={s.rungNote}>{r.note}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </Card>

      <div className={s.row2}>
        <Card title="Inventory, by placement" sub="The commercial registry. Stationary placements have never earned.">
          <Table
            cols={[
              { key: 'platform', label: 'Placement' },
              { key: 'city', label: 'City', muted: true },
              { key: 'lifecycle', label: 'Lifecycle', muted: true },
              { key: 'nodes', label: 'Nodes', num: true, render: (r) => n(r.nodes) },
            ]}
            rows={d.inventory}
            getKey={(r, i) => `${r.platform}-${r.city}-${r.lifecycle}-${i}`}
          />
          {d.stationary && <p className={s.warnText}>{d.stationary.note}</p>}
        </Card>

        <Card title="The fleet behind the screens">
          <dl className={s.metric}><dt>Vehicles on record</dt><dd>{n(d.fleet?.vehicles)}</dd></dl>
          <dl className={s.metric}><dt>Screens paired to a cab</dt><dd>{n(d.fleet?.activeAssignments)}</dd></dl>
          <dl className={s.metric}><dt>Active field devices (non-test)</dt><dd>{n(d.fleet?.fieldDevicesReal)}</dd></dl>
          <dl className={s.metric}><dt>Marked as test devices</dt><dd>{n(d.fleet?.fieldDevicesTest)}</dd></dl>
          <dl className={s.metric}><dt>SIMs active</dt><dd>{n(d.fleet?.simsActive)}</dd></dl>
          <dl className={s.metric}><dt>SIM cost carried</dt><dd>{d.fleet?.simsMonthly} / month</dd></dl>
        </Card>
      </div>

      <Card
        title="Every fitted cab, and when it last delivered"
        sub={`${(d.boundCabs || []).length} cabs carry a screen. ${d.dark?.over20Days || 0} have been dark for more than three weeks `
           + 'while the registry still reads deployed and active.'}
      >
        <Table
          cols={[
            { key: 'plate', label: 'Vehicle' },
            { key: 'city', label: 'City', muted: true },
            { key: 'bound_ist', label: 'Fitted', muted: true },
            { key: 'last_play_ist', label: 'Last delivered', render: (r) => r.last_play_ist || 'never' },
            { key: 'days_dark', label: 'Days dark', num: true,
              render: (r) => (r.days_dark == null ? '—' : n(r.days_dark)) },
          ]}
          rows={d.boundCabs}
          getKey={(r) => r.plate}
        />
        {d.dark?.note && <p className={s.warnText}>{d.dark.note}</p>}
      </Card>

      <Card
        title="Where the plays actually happen"
        sub="“Mumbai and Pune” is true. It is not the same claim as a two-city network."
      >
        <Table
          cols={[
            { key: 'city', label: 'City' },
            { key: 'screens', label: 'Screens', num: true, render: (r) => n(r.screens) },
            { key: 'plays', label: 'Plays, last 7 days', num: true, render: (r) => n(r.plays) },
            { key: 'share', label: 'Share', num: true,
              render: (r) => {
                const total = (d.cityConcentration || []).reduce((a, x) => a + x.plays, 0);
                return total ? pct((r.plays / total) * 100) : '—';
              } },
          ]}
          rows={d.cityConcentration}
          getKey={(r) => r.city}
        />
      </Card>

      <Card title="When each screen first delivered" sub="Cumulative, by the IST month a device first played an ad.">
        <Table
          cols={[
            { key: 'month', label: 'Month' },
            { key: 'new_devices', label: 'First delivered', num: true, render: (r) => n(r.new_devices) },
            { key: 'cumulative', label: 'Cumulative', num: true, render: (r) => n(r.cumulative) },
          ]}
          rows={d.growth}
          getKey={(r) => r.month}
        />
      </Card>
    </>
  );
}

/* ── 05 · what it earned ─────────────────────────────────────────────── */
function Money({ d }) {
  const series = d.runRate?.series || [];
  const peak = Math.max(1, ...series.map((x) => x.rupees));
  const r = d.regime || {};
  return (
    <>
      <Card
        title="Before and after the presence gate"
        sub={`The gate turned on ${new Date(r.cutover).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' })} IST. `
           + 'The cutover day is excluded from both sides so neither is contaminated by a mixed day.'}
      >
        <div className={s.row2}>
          <div className={s.regime}>
            <span className={s.regimeName}>Blanket — every play charged</span>
            <dl className={s.metric}><dt>Accrued</dt><dd>{inr0(r.blanket?.rupees)}</dd></dl>
            <dl className={s.metric}><dt>Plays</dt><dd>{n(r.blanket?.plays)}</dd></dl>
            <dl className={s.metric}><dt>Billable share</dt><dd>{pct(r.blanket?.billableShare)}</dd></dl>
            <dl className={s.metric}><dt>Realised CPM</dt><dd>{inr(r.blanket?.cpm)}</dd></dl>
          </div>
          <div className={`${s.regime} ${s.gated}`}>
            <span className={s.regimeName}>Gated — only a verified passenger</span>
            <dl className={s.metric}><dt>Accrued</dt><dd>{inr0(r.gated?.rupees)}</dd></dl>
            <dl className={s.metric}><dt>Plays</dt><dd>{n(r.gated?.plays)}</dd></dl>
            <dl className={s.metric}><dt>Billable share</dt><dd>{pct(r.gated?.billableShare)}</dd></dl>
            <dl className={s.metric}><dt>Realised CPM</dt><dd>{inr(r.gated?.cpm)}</dd></dl>
          </div>
        </div>
        <p className={s.cardSub}>
          Billable inventory fell by roughly two thirds and the price per verified impression did not move.
          That is the thesis of the company, measured rather than argued — and the reason it is the one number
          to lead with.
        </p>
      </Card>

      <Card title="Who funded the gated era" sub="This window is entirely after the portfolio split, so tenant attribution here is clean.">
        <Table
          cols={[
            { key: 'tenant', label: 'Portfolio' },
            { key: 'external', label: 'Kind', render: (x) => (x.external ? 'External brand' : 'Related party') },
            { key: 'rupees', label: 'Accrued', num: true, render: (x) => inr(x.rupees) },
            { key: 'plays', label: 'Plays', num: true, render: (x) => n(x.plays) },
            { key: 'share', label: 'Share', num: true, render: (x) => pct(x.share) },
          ]}
          rows={d.gatedByTenant}
          getKey={(x) => x.tenant}
        />
      </Card>

      <div className={s.row2}>
        <Card title="Cash, by payment rail" sub="The only thing in this database that evidences money actually moving.">
          <dl className={s.metric}><dt>Live gateway — top-ups</dt><dd>{n(d.cash?.live?.topups)}</dd></dl>
          <dl className={s.metric}><dt>Live gateway — value</dt><dd>{inr(d.cash?.live?.rupees)}</dd></dl>
          <dl className={s.metric}><dt>Test gateway — top-ups</dt><dd>{n(d.cash?.test?.topups)}</dd></dl>
          <dl className={s.metric}><dt>Test gateway — value</dt><dd>{inr(d.cash?.test?.rupees)}</dd></dl>
          <p className={s.warnText}>
            No bank statement, settlement file or UTR exists anywhere in this database. The only cash it can
            evidence is what came through the live rail.
          </p>
        </Card>

        <Card title="Brand wallets" sub={d.wallets?.note}>
          <Table
            cols={[
              { key: 'tenant', label: 'Brand' },
              { key: 'balance', label: 'Held', num: true, render: (x) => inr(x.balance) },
            ]}
            rows={d.wallets?.rows}
            getKey={(x) => x.tenant}
          />
          <dl className={s.metric}>
            <dt>Runway at the current burn</dt>
            <dd>{d.wallets?.runwayDays == null ? '—' : `${Math.floor(d.wallets.runwayDays)} days`}</dd>
          </dl>
        </Card>
      </div>

      <Card
        title="Every gated day"
        sub={`${series.length} complete IST days, bench screen excluded. Last 7 days ${inr(d.runRate?.last7?.perDay)}/day `
           + `against ${inr(d.runRate?.gatedEra?.perDay)}/day across the whole era — flat, not declining.`}
      >
        <div className={s.series}>
          {series.map((x) => (
            <div key={x.day} className={s.bar} style={{ height: `${(x.rupees / peak) * 100}%` }} title={`${x.label || x.day} · ${inr(x.rupees)} · ${x.cabs} cabs`} />
          ))}
        </div>
        <div className={s.seriesAxis}>
          <span>{series[0]?.label || series[0]?.day}</span>
          <span>{series[series.length - 1]?.label || series[series.length - 1]?.day}</span>
        </div>
      </Card>
    </>
  );
}

/* ── 04 · who paid ───────────────────────────────────────────────────── */
function Demand({ d }) {
  return (
    <>
      <Card title="The container, and the split" sub={d.split?.explain}>
        <div className={s.row2}>
          <div className={s.regime}>
            <span className={s.regimeName}>Before {d.split?.day} — inside the CabE portfolio</span>
            <Table
              cols={[
                { key: 'campaign', label: 'Campaign' },
                { key: 'rupees', label: 'Accrued', num: true, render: (x) => inr(x.rupees) },
                { key: 'plays', label: 'Plays', num: true, render: (x) => n(x.plays) },
              ]}
              rows={d.split?.preSplit}
              getKey={(x) => x.campaign}
            />
          </div>
          <div className={`${s.regime} ${s.gated}`}>
            <span className={s.regimeName}>From {d.split?.day} — each brand its own portfolio</span>
            <Table
              cols={[
                { key: 'tenant', label: 'Brand' },
                { key: 'campaign', label: 'Campaign' },
                { key: 'rupees', label: 'Accrued', num: true, render: (x) => inr(x.rupees) },
              ]}
              rows={(d.split?.postSplit || []).filter((x) => x.rupees > 0)}
              getKey={(x) => `${x.tenant}-${x.campaign}`}
            />
          </div>
        </div>
      </Card>

      <Card title="Campaigns built, by state" sub="The whole object inventory, including everything deleted and archived along the way.">
        <Table
          cols={[
            { key: 'status', label: 'State' },
            { key: 'campaigns', label: 'Campaigns', num: true, render: (x) => n(x.campaigns) },
            { key: 'published', label: 'Published', num: true, render: (x) => n(x.published) },
            { key: 'tenants', label: 'Portfolios', num: true, render: (x) => n(x.tenants) },
          ]}
          rows={d.campaigns}
          getKey={(x) => x.status}
        />
      </Card>
    </>
  );
}

/* ── 06 · what we keep ───────────────────────────────────────────────── */
function Split({ d }) {
  const u = d.unitEconomics || {};
  return (
    <>
      <Card
        title="Unit economics, on the denominator that does not flatter"
        sub={u.window}
      >
        <div className={s.row2}>
          <div className={s.regime}>
            <span className={s.regimeName}>A screen that worked</span>
            <dl className={s.metric}><dt>Per earning cab-day</dt><dd>{inr(u.perEarningCabDay)}</dd></dl>
            <dl className={s.metric}><dt>Cab-days that earned</dt><dd>{n(u.earningCabDays)} of {n(u.possibleCabDays)}</dd></dl>
            <dl className={s.metric}><dt>Utilisation</dt><dd>{pct(u.utilisationPct, 0)}</dd></dl>
          </div>
          <div className={`${s.regime} ${s.gated}`}>
            <span className={s.regimeName}>Scaled out, per month</span>
            <dl className={s.metric}><dt>Per cab we have fitted ({n(u.boundCabs)})</dt><dd>{inr(u.perBoundCabMonth)}</dd></dl>
            <dl className={s.metric}><dt>Per screen we own ({n(u.activeDevices)})</dt><dd>{inr(u.perActiveDeviceMonth)}</dd></dl>
          </div>
        </div>
        <p className={s.warnText}>{u.warning}</p>
      </Card>

      <Card
        title="Statements to supply partners"
        sub="The share ledger's own working, month by month. It is NOT what we owe — that is the Invoiced column, the document raised in the partner's name on presence-verified delivery. Nothing has been paid."
      >
        <Table
          cols={[
            { key: 'status', label: 'State' },
            { key: 'period', label: 'Month' },
            { key: 'party', label: 'Party' },
            { key: 'grossShare', label: 'Ledger gross', num: true, render: (x) => inr(x.grossShare) },
            { key: 'gatedAway', label: 'Gated away', num: true, render: (x) => inr(x.gatedAway) },
            { key: 'simExpense', label: 'SIM', num: true, render: (x) => inr(x.simExpense) },
            { key: 'ledgerResidue', label: 'Ledger residue', num: true, render: (x) => inr(x.ledgerResidue) },
            { key: 'invoiced', label: 'Invoiced (owed)', num: true, render: (x) => (x.invoicedDocuments ? inr(x.invoiced) : '—') },
            { key: 'paidAt', label: 'Paid', render: (x) => (x.paidAt ? new Date(x.paidAt).toLocaleDateString('en-IN') : 'never') },
          ]}
          rows={d.statements}
          getKey={(x) => `${x.status}-${x.period}`}
        />
      </Card>

      <Card title="The waterfall, as designed" sub={d.waterfall?.note}>
        <dl className={s.metric}><dt>Platform technology</dt><dd>{d.waterfall?.design?.platformTech}%</dd></dl>
        <dl className={s.metric}><dt>Platform margin</dt><dd>{d.waterfall?.design?.platformMargin}%</dd></dl>
        <dl className={s.metric}><dt>Premise operator</dt><dd>{d.waterfall?.design?.premiseOperator}%</dd></dl>
        <dl className={s.metric}><dt>Device owner</dt><dd>{d.waterfall?.design?.deviceOwner}%</dd></dl>
        <p className={s.warnText}>
          This is a design parameter, not a margin. There is no cost ledger in this database beyond the SIM
          line — no hardware, installation, field-ops or cloud cost — so payback per screen cannot be
          computed here and must not be offered.
        </p>
      </Card>
    </>
  );
}

/* ── 07 · what a cab can do ──────────────────────────────────────────── */
function Cab({ d }) {
  const sp = d.spread || {};
  const h = d.headroom || {};
  const dc = d.demandConstraint || {};
  const wkTop = Math.max(1, ...(d.weekday || []).map((w) => w.meanCabDay || 0));
  const cabTop = Math.max(1, ...(d.perCab || []).map((c) => c.meanDay || 0));
  return (
    <>
      <Card
        title="Every fitted cab, by what it earns on a working day"
        sub={`Gated era only, ${sp.days} complete IST days. Ordered by mean earning day — the rate that survives, `
           + 'not the single best day.'}
      >
        <div className={s.ladder}>
          {(d.perCab || []).map((c) => (
            <div className={s.rung} key={c.plate}>
              <span className={s.rungName}>{c.plate} <span className={s.muted}>· {c.city} · {n(c.earningDays)}d</span></span>
              <div className={s.rungBarWrap}>
                <div className={s.rungBar} style={{ width: `${(c.meanDay / cabTop) * 100}%` }} />
              </div>
              <span className={s.rungCount}>{inr(c.meanDay, 0)}</span>
              <span className={s.rungNote}>
                {inr(c.total, 0)} total · best day {inr(c.bestDay, 0)} · {n(c.plays)} plays
              </span>
            </div>
          ))}
        </div>
      </Card>

      <div className={s.row2}>
        <Card title="The ten best cab-days on record" sub="One cab, one day. Never multiply these out as a fleet rate.">
          <Table
            cols={[
              { key: 'day', label: 'Day' },
              { key: 'plate', label: 'Cab' },
              { key: 'city', label: 'City', muted: true },
              { key: 'rupees', label: 'Earned', num: true, render: (r) => inr(r.rupees) },
              { key: 'plays', label: 'Plays', num: true, render: (r) => n(r.plays) },
            ]}
            rows={d.bestDays}
            getKey={(r, i) => `${r.plate}-${i}`}
          />
        </Card>

        <Card title="The spread of a cab-day" sub={`${n(sp.cabDays)} working cab-days in the window.`}>
          <dl className={s.metric}><dt>Median</dt><dd>{inr(sp.p50)}</dd></dl>
          <dl className={s.metric}><dt>75th percentile</dt><dd>{inr(sp.p75)}</dd></dl>
          <dl className={s.metric}><dt>90th percentile</dt><dd>{inr(sp.p90)}</dd></dl>
          <dl className={s.metric}><dt>95th percentile</dt><dd>{inr(sp.p95)}</dd></dl>
          <dl className={s.metric}><dt>Best ever</dt><dd>{inr(sp.best)}</dd></dl>
          <p className={s.cardSub}>
            The gap between the median and the 75th is the honest improvement target. The best is a
            ceiling, not a plan.
          </p>
        </Card>
      </div>

      <Card title="By day of week" sub={d.weekdayCaveat}>
        <div className={s.ladder}>
          {(d.weekday || []).map((w) => (
            <div className={s.rung} key={w.dow}>
              <span className={s.rungName}>{w.dow} <span className={s.muted}>· {n(w.cabDays)} cab-days</span></span>
              <div className={s.rungBarWrap}>
                <div className={s.rungBar} style={{ width: `${(w.meanCabDay / wkTop) * 100}%` }} />
              </div>
              <span className={s.rungCount}>{inr(w.meanCabDay, 0)}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card
        title="What the cabs we already have could earn"
        sub={h.note}
      >
        <Table
          cols={[
            { key: 'scenario', label: 'Every fitted cab, every day, at…' },
            { key: 'perMonth', label: 'Per month', num: true, render: (r) => inr0(r.perMonth) },
            { key: 'multiple', label: 'vs today', num: true,
              render: (r) => (h.actualPerMonth ? `${(r.perMonth / h.actualPerMonth).toFixed(1)}×` : '—') },
          ]}
          rows={[
            { scenario: 'today, as it actually runs', perMonth: h.actualPerMonth },
            { scenario: 'the median cab-day', perMonth: h.atMedianPerMonth },
            { scenario: 'the 75th-percentile cab-day', perMonth: h.atP75PerMonth },
            { scenario: 'the best cab-day ever recorded', perMonth: h.atBestPerMonth },
          ]}
          getKey={(r) => r.scenario}
        />
        <dl className={s.metric}>
          <dt>Utilisation of fitted cabs</dt>
          <dd>{pct(h.utilisationOfFitted, 0)} — {n(h.fittedCabs)} cabs over {n(h.windowDays)} days</dd>
        </dl>
      </Card>

      <div className={s.guard}>
        <span className={s.guardTag}>Demand</span>
        <p className={s.guardText}>
          {dc.note} Advertisers hold {inr0(dc.walletsHeld)}: about {dc.daysAtActual == null ? '—' : Math.floor(dc.daysAtActual)} days
          at today's burn, and roughly {dc.daysAtMedianFull == null ? '—' : Math.floor(dc.daysAtMedianFull)} days
          if every fitted cab ran every day.
        </p>
      </div>
    </>
  );
}

const BODIES = { estate: Estate, money: Money, demand: Demand, split: Split, cab: Cab };

export default function Board({ screen = 'estate' }) {
  const { data, error, loading, reload } = useApi(
    () => api.get(`/insights/board/${screen}`).then((r) => r.data), [screen]);

  const Body = BODIES[screen];

  return (
    <div className={s.wrap}>
      <PageHead
        title={data?.title || 'Board'}
        sub="Super-admin reference for investor and advisor conversations. Every figure links back to the screen that owns it."
        actions={<Button onClick={reload}>Refresh</Button>}
      />
      {data?.asks && <p className={s.asks}>“{data.asks}”</p>}

      {loading && <Loading what="the board" />}
      {error && <ErrorState error={error} onRetry={reload} />}

      {data && !loading && (
        <>
          <p className={s.stamp}>
            <span>Read from production at {data.ist}</span>
            <span>·</span>
            <span>Figures move as the ledger accrues — quote them with this stamp, not from memory.</span>
          </p>
          <Headlines items={data.headline} />
          {Body && <Body d={data} />}
          {data.guard && (
            <div className={s.guard}>
              <span className={s.guardTag}>Guard</span>
              <p className={s.guardText}>{data.guard}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
